import {
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { isPlatformBrowser } from '@angular/common';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { User, UserCredential } from 'firebase/auth';
import { NGX_AUTH_FIREBASEUI_ANIMATIONS } from '../animations';
import { AuthProcessService } from '../auth-process.service';
import {
  INgAuthConfig,
  NG_AUTH_CONFIG_TOKEN,
} from '../ngx-auth-firebaseui-old/module-config';

export const confirmPasswordValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  if (!control.parent || !control) {
    // eslint-disable-next-line no-null/no-null
    return null;
  }

  const password = control.parent.get('password');
  const passwordConfirm = control.parent.get('passwordConfirm');

  if (!password || !passwordConfirm) {
    // eslint-disable-next-line no-null/no-null
    return null;
  }

  if (passwordConfirm.value === '') {
    // eslint-disable-next-line no-null/no-null
    return null;
  }

  if (password.value === passwordConfirm.value) {
    // eslint-disable-next-line no-null/no-null
    return null;
  }

  return { passwordsNotMatching: true };
};

interface IRegisterFormData {
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
  tos: string;
  privacyPolicy: string;
}

@Component({
  selector: 'pt-auth-firebaseui-register',
  templateUrl: './ngx-auth-firebaseui-register.component.html',
  styleUrls: ['./ngx-auth-firebaseui-register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: NGX_AUTH_FIREBASEUI_ANIMATIONS,
  standalone: false,
  providers: [AuthProcessService],
})
export class NgxAuthFirebaseuiRegisterComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();

  @Input() logoUrl: string;
  @Input() appearance: MatFormFieldAppearance;
  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;

  // i18n common
  @Input() titleText = 'CREATE AN ACCOUNT';
  @Input() termsAndConditionsText = 'I read and accept the';
  @Input() termsAndConditionsLinkText = 'terms and conditions';
  @Input() privacyPolicyText = 'I read and accept the';
  @Input() privacyPolicyLinkText = 'privacy policy';
  @Input() createAccountButtonText = 'CREATE AN ACCOUNT';
  @Input() alreadyHaveAccountText = 'Already have an account?';
  @Input() loginButtonText = 'LOGIN';

  // i18n name
  @Input() nameText = 'Name';
  @Input() nameErrorRequiredText = 'Name is required';

  // i18n email
  @Input() emailText = 'Email';
  @Input() emailErrorRequiredText = 'Email is required';
  @Input() emailErrorPatternText = 'Please enter a valid email address';

  // i18n password
  @Input() passwordText = 'Password';
  @Input() passwordErrorRequiredText = 'Password is required';
  @Input() passwordConfirmationText = 'Password Confirmation';
  @Input() passwordConfirmationErrorRequiredText =
    'Password confirmation is required';
  @Input() passwordErrorMatchText = 'Password must match';
  @Input() passwordErrorMinLengthText = 'The password is too short!';
  @Input() passwordErrorMaxLengthText = 'The password is too long!';

  // Events
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSuccess: EventEmitter<UserCredential | User>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onError: EventEmitter<Error>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onLoginRequested = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onCreateAccountButtonClicked = new EventEmitter<void>();

  registerForm: TypedFormGroup<IRegisterFormData>;
  authenticationError = false;

  constructor(
    @Inject(PLATFORM_ID) private _platformId: object,
    @Inject(forwardRef(() => NG_AUTH_CONFIG_TOKEN))
    public config: INgAuthConfig,
    public authProcess: AuthProcessService
  ) {
    this.onSuccess = authProcess.onSuccessEmitter;
    this.onError = authProcess.onErrorEmitter;

    if (isPlatformBrowser(this._platformId)) {
      this.onError
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(() => (this.authenticationError = true));
    }
    this.registerForm = new TypedFormGroup({
      name: new TypedFormControl('', Validators.required),
      email: new TypedFormControl('', [Validators.required, Validators.email]),
      password: new TypedFormControl('', [
        Validators.required,
        Validators.minLength(this.config.passwordMinLength || 6),
        Validators.maxLength(this.config.passwordMaxLength || 25),
      ]),
      passwordConfirm: new TypedFormControl('', [
        Validators.required,
        confirmPasswordValidator,
      ]),
      tos: new TypedFormControl(''),
      privacyPolicy: new TypedFormControl(''),
    });

    // If tos or privacy policy url set, ensure that the two form items are required
    if (this.tosUrl) {
      this.registerForm.controls.tos.setValidators(Validators.requiredTrue);
    }

    if (this.privacyPolicyUrl) {
      this.registerForm.controls.privacyPolicy.setValidators(
        Validators.requiredTrue
      );
    }

    // Update the validity of the 'passwordConfirm' field
    // when the 'password' field changes
    this.registerForm.controls.password.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.registerForm.controls.passwordConfirm.updateValueAndValidity();
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  get color(): string {
    return this.authenticationError ? 'warn' : 'primary';
  }

  get colorAccent(): string {
    return this.authenticationError ? 'warn' : 'accent';
  }

  async createAccount(): Promise<void> {
    this.onCreateAccountButtonClicked.emit();
    const data = this.registerForm.getRawValue();

    await this.authProcess.signUp(data.name, {
      email: data.email,
      password: data.password,
    });
  }
}
